import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Receipt } from 'lucide-react'
import { listInvoices } from '../../api/invoices'
import { ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox } from '../../components/ui/Card'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass, skeletonClass, filterInputClass } from '@/lib/tableStyles'
import { formatCurrency } from '@/lib/currency'
import type { InvoiceListItem, InvoiceStatus } from '../../types/invoices'
import { STATUS_COLORS, STATUS_LABELS } from '../../types/invoices'

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}
function firstOfMonthISO(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const { color, bg } = STATUS_COLORS[status]
  return (
    <span
      className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
      style={{ color, background: bg }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

export default function InvoiceList() {
  const [searchParams] = useSearchParams()
  const initialStatus = searchParams.get('status') ?? ''

  // A status deep-link (e.g. from a dashboard widget) means "show me every matching
  // invoice regardless of age" — the default this-month range would otherwise hide
  // older overdue invoices, which is exactly what that link is trying to surface.
  const [dateFrom, setDateFrom] = useState(initialStatus ? '' : firstOfMonthISO())
  const [dateTo, setDateTo] = useState(initialStatus ? '' : todayISO())
  const [statusFilter, setStatusFilter] = useState(initialStatus)
  const [clientSearch, setClientSearch] = useState('')

  const { data, loading, error } = useFetch(
    () => listInvoices({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      status: statusFilter || undefined,
      limit: 200,
    }),
    [dateFrom, dateTo, statusFilter],
  )

  const allInvoices: InvoiceListItem[] = data ?? []
  const invoices = clientSearch
    ? allInvoices.filter(inv => (inv.client_name ?? '').toLowerCase().includes(clientSearch.toLowerCase()))
    : allInvoices

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Invoices</h1>
        <ButtonLink to="/invoices/new">+ New Invoice</ButtonLink>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="search"
          className={`${filterInputClass} min-w-[180px]`}
          placeholder="Search client…"
          value={clientSearch}
          onChange={e => setClientSearch(e.target.value)}
        />
        <div className="flex items-center gap-[0.4rem]">
          <label className="text-[0.82rem] text-text-2 whitespace-nowrap">From</label>
          <input type="date" className={filterInputClass} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="flex items-center gap-[0.4rem]">
          <label className="text-[0.82rem] text-text-2 whitespace-nowrap">To</label>
          <input type="date" className={filterInputClass} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <select
          className={`${filterInputClass} cursor-pointer`}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {(Object.keys(STATUS_LABELS) as InvoiceStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <button
          className="bg-transparent border-none text-text-2 text-[0.82rem] cursor-pointer underline p-0"
          onClick={() => { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); setStatusFilter(''); setClientSearch('') }}
        >
          Reset
        </button>
      </div>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Invoice #</th>
                <th className={thClass}>Client</th>
                <th className={thClass}>Patient</th>
                <th className={thClass}>Issued</th>
                <th className={thClass}>Due</th>
                <th className={`${thClass} text-right`}>Amount</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}></th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className={`${skeletonClass} h-3.5 ${j === 7 ? 'w-12' : 'w-4/5'}`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={8} className={tdClass}>
                    <div className="text-center py-12 px-4 text-text-3">
                      <Receipt size={36} strokeWidth={1.25} className="text-text-4 mb-2 mx-auto" />
                      <div className="font-semibold text-text-heading mb-[0.35rem]">No invoices found</div>
                      <div className="text-sm mb-4">
                        {statusFilter || dateFrom || dateTo ? 'Try adjusting the filters' : 'Create your first invoice to get started'}
                      </div>
                      <ButtonLink to="/invoices/new">+ New Invoice</ButtonLink>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map(inv => (
                  <tr key={inv.id}>
                    <td className={`${tdClass} font-semibold`}>
                      <Link to={`/invoices/${inv.id}`} className="text-accent no-underline hover:underline">
                        <span className="font-mono text-sm">{inv.invoice_number}</span>
                      </Link>
                    </td>
                    <td className={tdClass}>
                      {inv.client_name ? (
                        <Link to={`/clients/${inv.client_id}`} className="text-text no-underline">
                          {inv.client_name}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className={`${tdClass} text-text-2`}>
                      {inv.patient_name ? (
                        <Link to={`/patients/${inv.patient_id}`} className="text-text-2 no-underline">
                          {inv.patient_name}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className={`${tdClass} text-text-2 whitespace-nowrap`}>{formatDate(inv.issued_date)}</td>
                    <td className={`${tdClass} whitespace-nowrap ${inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid' ? 'text-danger-text' : 'text-text-2'}`}>
                      {inv.due_date ? formatDate(inv.due_date) : '—'}
                    </td>
                    <td className={`${tdClass} text-right font-semibold`}>{formatCurrency(inv.total_amount)}</td>
                    <td className={tdClass}><StatusBadge status={inv.status} /></td>
                    <td className={`${tdClass} text-right`}>
                      <Link to={`/invoices/${inv.id}`} className="text-accent font-medium text-[0.82rem] no-underline">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
